/**
 * Shared language gate — used by Review Shield, Surveys public, and Landing.
 *
 * Behaviour (matches spec 2a / 3a / 3b):
 *   • If enabled_languages.length <= 1 → picker is skipped; default locale applied.
 *   • If enabled_languages.length > 1  → picker shown on first visit.
 *   • Customer choice persisted in localStorage under `scopeKey`.
 *   • On every load: validate stored preference against current enabled list.
 *     Stale preference (e.g. lang disabled since last visit) → re-prompt (3b).
 *   • Once chosen, a small <ChangeLanguageLink> is available at top-right (2a).
 */
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import BrandingShell from './BrandingShell';
import { cardSurfaceClass } from './cardSurface';

const LANGUAGE_LABELS = {
    en: { native: 'English', flag: '🇬🇧' },
    fr: { native: 'Français', flag: '🇫🇷' },
    es: { native: 'Español', flag: '🇪🇸' },
};

const storageKey = (scopeKey) => `yulontap_lang_pref:${scopeKey}`;

/**
 * Hook: returns {language, needsPicker, choose, reset} for a given scope.
 * - `enabled`: resolved list from backend
 * - `scopeKey`: unique string per card/survey (e.g. `rs:DEL003`, `s:demo-restroom`)
 */
export function useLanguageGate(enabled, scopeKey) {
    const { i18n } = useTranslation();
    const [language, setLanguage] = useState(null);
    const [needsPicker, setNeedsPicker] = useState(false);

    useEffect(() => {
        if (!enabled || enabled.length === 0) return;

        if (enabled.length === 1) {
            const only = enabled[0];
            setLanguage(only);
            setNeedsPicker(false);
            try { i18n.changeLanguage(only); } catch {}
            return;
        }

        let stored = null;
        try { stored = localStorage.getItem(storageKey(scopeKey)); } catch {}

        if (stored && enabled.includes(stored)) {
            setLanguage(stored);
            setNeedsPicker(false);
            try { i18n.changeLanguage(stored); } catch {}
            return;
        }

        // stale preference → clear + re-prompt
        if (stored) {
            try { localStorage.removeItem(storageKey(scopeKey)); } catch {}
        }
        setLanguage(null);
        setNeedsPicker(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(enabled), scopeKey]);

    const choose = useCallback((lang) => {
        if (!enabled?.includes(lang)) return;
        try { localStorage.setItem(storageKey(scopeKey), lang); } catch {}
        setLanguage(lang);
        setNeedsPicker(false);
        try { i18n.changeLanguage(lang); } catch {}
    }, [enabled, scopeKey, i18n]);

    const reset = useCallback(() => {
        try { localStorage.removeItem(storageKey(scopeKey)); } catch {}
        setNeedsPicker(true);
    }, [scopeKey]);

    return { language, needsPicker, choose, reset };
}

/**
 * Full-screen welcome / language selection card.
 * Rendered when `needsPicker === true`. Identical visual across Review Shield,
 * Surveys, and Landing — this is the single source of truth.
 *
 * `embedded={true}` — caller already provides a BrandingShell (or its own
 *                     centering / background). LanguageGate then renders ONLY
 *                     the card; no nested min-h-screen wrappers, no double
 *                     gradients.
 *
 * `wallpaperUrl`   — optional. When provided AND not embedded, LanguageGate
 *                     wraps itself in BrandingShell with the wallpaper. When
 *                     embedded, this prop is ignored (the parent shell handles
 *                     the background).
 */
export function LanguageGate({
    enabled,
    onSelect,
    variant = 'light',
    orgName,
    locationName,
    logoUrl,
    logoSize = 'medium',
    embedded = false,
    wallpaperUrl = null,
    cardStyle = 'frosted',
}) {


    // v3.5.11 — Inline logo above the welcome heading (parity with Review Shield).
    const LOGO_SIZE_CLASSES = {
        small:      'max-h-16 max-w-[140px]',
        medium:     'max-h-24 max-w-[200px]',
        large:      'max-h-36 max-w-[300px]',
        very_large: 'max-h-48 max-w-[400px]',
    };
    const titles = {
        en: 'Choose your language',
        fr: 'Choisissez votre langue',
        es: 'Elige tu idioma',
    };
    const combinedTitle = enabled
        .map((code) => titles[code])
        .filter(Boolean)
        .join(' · ');

    const isDark = variant === 'dark';
    // v3.5.17 — Background + centering chrome moved into BrandingShell.
    // LanguageGate only owns its card from now on. Embedded callers wrap
    // themselves; non-embedded callers get a default BrandingShell.
    // v3.5.19 — Card surface (frosted/clear/solid) is admin-controlled per
    // org, with optional per-card override. Helper resolves to one of three
    // Tailwind class strings; fallback identical to v3.5.18 frosted look.
    const surface = cardSurfaceClass(cardStyle, isDark ? 'dark' : 'light');
    const cardClass = `${surface} rounded-2xl p-6 w-full max-w-md mx-auto`;
    const titleClass = isDark
        ? 'text-xl font-semibold text-white text-center mb-6'
        : 'text-xl font-semibold text-slate-900 dark:text-white text-center mb-6';

    const card = (
        <div className={cardClass} data-testid="language-gate-card" data-card-style={cardStyle}>
                {logoUrl && (
                    <div className="flex justify-center mb-5" data-testid="language-gate-logo-wrapper">
                        <img
                            src={logoUrl}
                            alt=""
                            loading="lazy"
                            className={`block object-contain ${LOGO_SIZE_CLASSES[logoSize] || LOGO_SIZE_CLASSES.medium}`}
                            data-testid="language-gate-logo"
                        />
                    </div>
                )}
                {(orgName || locationName) && (
                    <div className="text-center mb-5">
                        {orgName && (
                            <p className={isDark ? 'text-emerald-400/70 text-xs uppercase tracking-widest' : 'text-fuchsia-600/80 dark:text-fuchsia-400 text-xs uppercase tracking-widest'}>
                                {orgName}
                            </p>
                        )}
                        {locationName && (
                            <p className={isDark ? 'text-slate-400 text-sm' : 'text-slate-500 dark:text-slate-300 text-sm'}>
                                {locationName}
                            </p>
                        )}
                    </div>
                )}
                <h1 className={titleClass} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {combinedTitle || 'Choose your language'}
                </h1>
                <div className="space-y-3">
                    {enabled.map((code) => {
                        const label = LANGUAGE_LABELS[code] || { native: code, flag: '🌐' };
                        const btnClass = isDark
                            ? 'w-full py-4 text-base font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-3'
                            : 'w-full py-4 text-base font-semibold rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white shadow-md transition-all duration-200 hover:scale-[1.01] flex items-center justify-center gap-3';
                        return (
                            <button
                                key={code}
                                type="button"
                                onClick={() => onSelect(code)}
                                className={btnClass}
                                data-testid={`language-gate-option-${code}`}
                                aria-label={label.native}
                            >
                                <span className="text-xl" aria-hidden="true">{label.flag}</span>
                                <span>{label.native}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
    );

    if (embedded) {
        return <div className="w-full" data-testid="language-gate-screen">{card}</div>;
    }
    return (
        <BrandingShell wallpaperUrl={wallpaperUrl} variant={isDark ? 'dark' : 'light'} data-testid="language-gate-screen">
            {card}
        </BrandingShell>
    );
}

/**
 * Small "Change language" link — rendered top-right on every public page.
 * Only shown when more than one language is enabled. Clicking triggers
 * `onOpen` which clears the stored pref and re-shows the picker.
 */
export function ChangeLanguageLink({ enabled, onOpen, variant = 'light' }) {
    if (!enabled || enabled.length <= 1) return null;
    const isDark = variant === 'dark';
    const cls = isDark
        ? 'absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors'
        : 'absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-white/90 dark:bg-slate-800/90 hover:bg-white dark:hover:bg-slate-700 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm transition-colors';
    return (
        <button
            type="button"
            onClick={onOpen}
            className={cls}
            data-testid="change-language-link"
            aria-label="Change language"
        >
            <Globe className="w-3.5 h-3.5" />
            <span className="font-medium">
                {enabled.map((c) => LANGUAGE_LABELS[c]?.native?.slice(0, 2) || c).join(' · ')}
            </span>
        </button>
    );
}

export const ENABLED_LANGUAGES_FALLBACK = ['en', 'fr', 'es'];

export default LanguageGate;
